/**
 * This page is cloned from the invoices page so it might
 * have similar naming conventions to the invoices page
 *
 */

import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { NavLink as Link } from "react-router-dom";
import { endpoints } from "../../hooks/endpoints";
import {
  convertText,
  dateFormatter,
  getAllowedCategoriesForPermission,
  getToken,
} from "../../hooks/utils";
import { useAppSelector } from "../../store/hooks";
import { pushModal } from "../../store/slices/modalsSlice";
import Animation from "../Animation";
import GeneralMessageModal from "../Modals/GeneralMessageModal";
import Spinner from "../Spinner";
import DynamicCategorySelector from "./DynamicCategorySelector";

const TeacherSales = () => {
  const [invoices, setInvoices] = useState<any | null>(null);
  const [currentCat, setCurrentCat] = useState<string>();
  const [currentLesson, setCurrentLesson] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string>("order_id");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);

  const portal = useAppSelector((state) => state.store.portal);

  const allowedCatsForSales =
    getAllowedCategoriesForPermission("can_view_sales");

  const cats = allowedCatsForSales
    ? portal?.cats?.filter((cat) => allowedCatsForSales.includes(cat.term_id))
    : portal?.cats;

  const lessons = portal?.data?.filter(
    (lesson) => Number(lesson.term_taxonomy_id) === Number(currentCat)
  );

  const dispatch = useDispatch();

  const fetchInvoices = async (page: number = currentPage) => {
    if (!currentCat) return;

    const baseEndpoint =
      currentLesson === "all"
        ? `${endpoints.teacherSalesByCategory}/${currentCat}`
        : `${endpoints.teacherSalesByProduct}/${currentLesson}`;

    const params: Record<string, string> = {
      page: page.toString(),
      page_size: pageSize.toString(),
      orderby: orderBy,
      order: order,
    };

    // Add date range parameters if provided
    if (dateFrom) {
      params.date_from = dateFrom;
    }
    if (dateTo) {
      params.date_to = dateTo;
    }

    const queryParams = new URLSearchParams(params);
    const endpoint = `${baseEndpoint}?${queryParams.toString()}`;

    setIsPending(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      });

      const res = await response.json();

      if (!response.ok) {
        setError(res.message);
        setInvoices([]);
        setIsPending(false);
        return;
      }

      setInvoices(res.data.invoices || []);
      setTotalItems(res.data.pagination?.total_items || 0);
    } catch (e) {
      console.error("Error fetching invoices:", error);
      setError(error);
      setInvoices([]);
    } finally {
      setIsPending(false);
    }
  };

  const handleSort = (column: string) => {
    if (orderBy === column) {
      // Toggle order if same column
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      // Set new column with default desc order
      setOrderBy(column);
      setOrder("desc");
    }
    setCurrentPage(1);
  };

  const handleGetInvoices = async (e: any) => {
    e.preventDefault();
    if (!currentCat)
      return dispatch(
        pushModal(
          <GeneralMessageModal msg="يجب إختيار المجموعة" btnMsg="حسنا" />
        )
      );

    setCurrentPage(1);
    fetchInvoices(1);
  };

  // Fetch invoices when page changes
  useEffect(() => {
    if (currentCat && invoices !== null) {
      fetchInvoices(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch invoices when sorting or date range changes
  useEffect(() => {
    if (currentCat && invoices !== null) {
      setCurrentPage(1);
      fetchInvoices(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order, dateFrom, dateTo]);

  // Reset when lesson changes
  useEffect(() => {
    if (invoices) {
      setInvoices(null);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLesson]);

  return (
    <Animation>
      <div className="space-y-6 max-w-full">
        <h3 className="bg-primary p-3 text-white rounded-lg shadow font-bold">
          مبيعاتي
        </h3>
        <div className="overflow-x-auto bg-white border rounded-lg shadow-sm p-3">
          <form onSubmit={handleGetInvoices} className="mb-4 space-y-3">
            <div className="md:w-3/4 w-full">
              <DynamicCategorySelector
                filteredCats={cats}
                excludeCatsThatHasLessons={false}
                categoryUpdateHanlderFunction={(value, chosenCatsIds) =>
                  setCurrentCat(value)
                }
              />
            </div>

            {currentCat && lessons?.length > 0 && (
              <select
                onChange={(e) => setCurrentLesson(e.target.value)}
                className="block md:w-3/4 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary-color-light)"
                required
              >
                <option value="all" selected>
                  كل الحصص
                </option>
                {lessons.map((lesson) => (
                  <option key={lesson.ID} value={lesson.ID}>
                    {convertText(lesson.post_title)}
                  </option>
                ))}
              </select>
            )}

            <div className="grid md:grid-cols-2 gap-3 md:w-3/4 w-full">
              <div>
                <label
                  htmlFor="dateFrom"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  من تاريخ
                </label>
                <input
                  type="date"
                  id="dateFrom"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="dateTo"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  إلى تاريخ
                </label>
                <input
                  type="date"
                  id="dateTo"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg transition hover:cursor-pointer hover:bg-primary/90"
              disabled={isPending}
            >
              عرض التقرير
            </button>
          </form>

          {!isPending && invoices && (
            <>
              {invoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 border border-gray-200 text-right">
                    <thead className="bg-gray-100">
                      <tr>
                        <th
                          className="px-4 py-2 text-sm font-semibold block text-nowrap"
                        >
                          رقم العملية
                        </th>
                        <th className="px-4 py-2 text-sm font-semibold">
                          الحصة
                        </th>
                        <th className="px-4 py-2 text-sm font-semibold">
                          الطالب
                        </th>
                        <th className="px-4 py-2 text-sm font-semibold">
                          رقم الطالب
                        </th>
                        <th
                          className="px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-gray-200 transition"
                          onClick={() => handleSort("order_id")}
                        >
                          التاريخ{" "}
                          {orderBy === "order_id" &&
                            (order === "desc" ? "↓" : "↑")}
                        </th>
                        <th className="px-4 py-2 text-sm font-semibold">
                          الإجمالي
                        </th>
                        <th className="px-4 py-2 text-sm font-semibold">
                          تفاصيل
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoices.map((invoice) => (
                        <tr
                          key={invoice.order_id}
                          className="hover:bg-gray-50 whitespace-nowrap"
                        >
                          <td className="px-4 py-2">{invoice.order_id}</td>
                          <td className="px-4 py-2">
                            {convertText(
                              lessons?.find((lesson) =>
                                currentLesson === "all"
                                  ? +lesson.ID === +invoice.product_id
                                  : +lesson.ID === +currentLesson
                              )?.post_title
                            )}
                          </td>
                          <td className="px-4 py-2">{invoice.user_name}</td>
                          <td className="px-4 py-2">{invoice.user_phone}</td>
                          <td className="px-4 py-2">
                            {dateFormatter(invoice.date_created)}
                          </td>
                          <td className="px-4 py-2">
                            {invoice.order_total} جنيه
                          </td>
                          <td className="px-4 py-2">
                            <Link
                              to={`/purchases/${invoice.order_id}`}
                              className="inline-block px-3 py-1 bg-primary text-white rounded-lg hover:bg-secondary transition"
                            >
                              تفاصيل
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center font-semibold text-lg text-primary my-4 w-full">
                  لا يوجد نتائج
                </p>
              )}

              <div className="flex gap-2 mt-4 items-center">
                {currentPage * pageSize < totalItems && (
                  <button
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition hover:cursor-pointer"
                    disabled={isPending}
                  >
                    التالي
                  </button>
                )}
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition hover:cursor-pointer"
                    disabled={isPending}
                  >
                    السابق
                  </button>
                )}
                {totalItems > 0 && (
                  <span className="text-sm text-gray-600">
                    صفحة {currentPage} من {Math.ceil(totalItems / pageSize)} (
                    {totalItems} نتيجة)
                  </span>
                )}
              </div>

              {error && (
                <h2 className="text-red-600 mt-4">يجب تسجيل الدخول اولا</h2>
              )}
            </>
          )}

          {isPending && (
            <div className="grow flex justify-center items-center">
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </Animation>
  );
};

export default TeacherSales;
